import React, { useState } from 'react'
import ConfirmDialog from '../../core/ConfirmDialog'
import SignaturePad from '../../core/SignaturePad'
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn'
import { Button, IconButton } from '@mui/material'
import logo from '../../../assets/images/logo-header.png'
import { useProjectFeaturesContext } from '../../project/contexts/ProjectFeaturesContext'
import { useConsentContext } from './ConsentContext'

type ConsentDialogProps = {
	type: 'dayStartPrompt' | 'dayEndPrompt'
	workerId: number
	error?: boolean
	onConsentChange?: () => void
}

const ConsentDialog = ({ type, workerId, error, onConsentChange }: ConsentDialogProps) => {
	const [open, setOpen] = useState(false)
	const { projectTimeZone } = useProjectFeaturesContext()
	const { consentTexts, getSignature, addSignature, removeSignature } = useConsentContext()
	const [currentSignatureURL, setCurrentSignatureURL] = useState<string>('')
	const [currentLines, setCurrentLines] = useState<Array<{ points: number[] }>>([])
	const [draft, setDraft] = useState(false)
	const isSigned = draft || !!getSignature(workerId)
	const handleConfirm = () => {
		addSignature(workerId, currentSignatureURL, currentLines)
		onConsentChange?.()
		setDraft(true)
		setOpen(false)
	}

	return (
		<>
			{isSigned ? (
				<IconButton
					size='small'
					color='primary'
					onClick={() => setOpen(true)}
					aria-label='View signed consent form'
				>
					<AssignmentTurnedInIcon />
				</IconButton>
			) : (
				<Button
					variant='text'
					size='small'
					sx={{
						color: error ? 'error.main' : 'primary.main',
						textDecoration: 'underline',
						'&:hover': {
							color: error ? 'error.dark' : 'primary.dark',
						},
					}}
					onClick={() => setOpen(true)}
				>
					Sign Form
				</Button>
			)}
			<ConfirmDialog
				title={`${type === 'dayStartPrompt' ? 'Day Start' : 'Day End'} Consent Form`}
				confirmButtonText={'Confirm'}
				cancelButtonText={'Cancel'}
				visible={open}
				onConfirm={handleConfirm}
				onCancel={() => {
					setOpen(false)
				}}
			>
				<SignaturePad
					formText={consentTexts[type] || ''}
					formLogo={logo}
					onSign={(url, lines) => {
						setCurrentSignatureURL(url)
						setCurrentLines(lines)
					}}
					date={new Date().toLocaleDateString('en-US', { timeZone: projectTimeZone })}
					initialLines={getSignature(workerId)}
					onClear={() => {
						setCurrentSignatureURL('')
						setCurrentLines([])
						setDraft(false)
						removeSignature(workerId)
					}}
				/>
			</ConfirmDialog>
		</>
	)
}

export default ConsentDialog
